/* Project Status Report — the printable / PDF export of a project's Job Costs (PSR #14).

   Design: the Claude Design hand-off "design_handoff_project_status_report" (Oct 2026), built
   to its spec. US Letter sheets at 816 × 1056 px: page 1 is the summary and never grows; the
   cost detail, then travel / notes / labor hours / change orders, flow onto the pages after it.

   The sheets are laid out HERE rather than by the browser's print engine. Once the data and
   fonts are in, everything is rendered off-screen at the content width and measured: page 1
   keeps as many phase rows as fit above the footer (the rest continue on page 2; the total
   stays on page 1), and the flowing sections break only between table rows, repeating the
   table head and adding "(continued)" to the title. Each sheet draws its own running header
   and "Page N of M" footer, so printing just stacks the sheets.

   Route /projects/:idOrSlug/report; `?print=1` opens the print dialog once it's laid out.
   It renders in a portal on <body>, outside the app's `.app.dark` container, and the print
   CSS hides the app (#root). Figures come from the Job Costs builders (buildFinancials /
   buildPhases / buildMonthly); billing reads CE's invoices, which are right for every job,
   rather than Summit billing, which only a few jobs use. */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getPMList, emailPsr } from '../api';
import { daysUntil, healthKey } from '../components/jobcost/JobCostShared';
import { downloadPsrWorkbook, fmtDay, loadPsrData, loadPsrFonts, localDay, ymd } from '../lib/psrData';
import { nowOn } from '../lib/projectToday';

/* ── design tokens (hand-off README) ── */
const INK = '#1F1D1A';
const INK2 = '#4A463F';
const GREY = '#6B665E';
const GREY_PH = '#767676';
const LINE = '#D6D2CA';
const LINE_STRONG = '#9D978D';
const FILL = '#F5F3EF';
const GREEN_HEAD = '#41593A';
const GREEN_SPENT = '#5A7A4D';
const GREEN_GOOD = '#3F7A57';
const AMBER = '#94601F';
const ORANGE = '#B4691F';
const RED = '#B3402E';
const BILLED = '#CFCAC1';
const SANS = "'Geist', system-ui, sans-serif";
const MONO = "'Geist Mono', 'SF Mono', Menlo, Consolas, monospace";

/* ── page geometry ── */
const PAGE_W = 816;
const PAGE_H = 1056;
const PAD_TOP = 54;
const PAD_X = 60;
const CONTENT_W = PAGE_W - 2 * PAD_X; // 696
// The footer rule sits at y 991 and content ends at least 16px above it. Measured from the
// top of the content box (y 54), less 4px of slack for rounding.
const CONTENT_BOTTOM = 991 - 16 - PAD_TOP - 4;

/* ── formatting ── */
const MINUS = '−';
const money = (v, { signed = false, cents = true } = {}) => {
  const n = Number(v) || 0;
  const r = cents ? Math.round(n * 100) / 100 : Math.round(n);
  const abs = Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
  if (r < 0) return `${MINUS}$${abs}`;
  return `${signed && r > 0 ? '+' : ''}$${abs}`;
};
const whole = (v, o) => money(v, { ...o, cents: false });
const hrs = (v) => {
  const r = Math.round((Number(v) || 0) * 100) / 100;
  const s = Math.abs(r).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return r < 0 ? MINUS + s : s;
};
const pctText = (p) => (p == null ? '—' : `${(Math.round(p * 1000) / 10).toFixed(1)}%`);
// A real timestamp (a note's created_at) is a moment, so it takes the local day it fell on.
const whenDay = (v) => {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return localDay(v);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Status colour for a spent / used ratio (bar and figure): red over 100%, amber 85–100%,
   green below. */
const statusColor = (p) => (p == null ? GREY : p > 1 ? RED : p >= 0.85 ? AMBER : GREEN_GOOD);
const overColor = (v) => (v > 0.005 ? RED : v < -0.005 ? GREEN_GOOD : INK);
// Summit's own status rule (healthKey), in the report's words.
const HEALTH = {
  over: ['Over budget', RED], atbudget: ['At budget', GREY], at: ['At risk', AMBER],
  on: ['On track', GREEN_GOOD], under: ['Under budget', GREEN_GOOD], none: ['No budget', GREY],
};

/* ── small pieces ── */
const sectionTitle = { fontSize: 12, lineHeight: '16px', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN_HEAD };
const numCell = (fs = 12) => ({ textAlign: 'right', fontFamily: MONO, fontVariantNumeric: 'tabular-nums', fontSize: fs });
/* Geist Mono is 0.6em a character. A table's figures stay 12px unless one of them would not
   fit its column (the design's narrow Committed and Over / under columns top out at seven
   figures); then the whole table steps down, half a pixel at a time, to 10px at the least.
   pairs: [text, room in px]. */
const fitFont = (pairs) => {
  for (let fs = 12; fs > 10; fs -= 0.5) if (pairs.every(([t, w]) => t.length * 0.6 * fs <= w)) return fs;
  return 10;
};
const mono = { fontFamily: MONO, fontVariantNumeric: 'tabular-nums' };
const Tag = () => (
  <span style={{ flex: 'none', display: 'inline-block', fontSize: 12, lineHeight: '16px', fontWeight: 600, letterSpacing: '.16em', color: RED, border: `1px solid ${RED}`, borderRadius: 2, padding: '2px 6px 2px 8px' }}>CONFIDENTIAL</span>
);
/* A track with a fill. No ratio or 0 → an empty track; tiny values keep a 1px sliver. */
const Bar = ({ pct, color, w, h = 6 }) => (
  <span style={{ position: 'relative', flex: 'none', width: w, height: h, background: LINE }}>
    {pct > 0 && <span style={{ position: 'absolute', left: 0, top: 0, height: h, width: `${Math.min(100, pct * 100)}%`, minWidth: 1, background: color }} />}
  </span>
);
/* "106.4% ▬▬" — the figure and its bar, right-aligned (the spent and used columns). */
const RatioCell = ({ text, pct, color, textColor, barW, bold, fs = 12 }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
    <span style={{ ...mono, fontSize: fs, fontWeight: bold ? 600 : 400, color: textColor || color }}>{text}</span>
    <Bar pct={pct} color={color} w={barW} />
  </div>
);
const SectionHead = ({ title, note, continued }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
    <div style={sectionTitle}>{title}{continued && <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: GREY }}> (continued)</span>}</div>
    {note && <div style={{ fontSize: 12, lineHeight: '16px', color: GREY }}>{note}</div>}
  </div>
);
const EmptyLine = ({ children }) => <div style={{ marginTop: 8, fontSize: 13, lineHeight: '18px', color: GREY }}>{children}</div>;

/* Table plumbing. Outer cells pad 0 at the table's edges; total rows (and the detail table's
   code rows) pad 6px there so their fill clears the text and the bars line up. Every border
   is set here because App.css styles bare th / td (a 1px td underline, a 2px th rule). */
const thStyle = (i, n, col) => ({
  fontSize: 12, lineHeight: '16px', letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 500, color: GREY,
  borderBottom: `1px solid ${LINE_STRONG}`, padding: i === 0 ? '0 4px 5px 0' : i === n - 1 ? '0 0 5px 4px' : '0 4px 5px',
  textAlign: col.align || 'left', width: col.w, whiteSpace: 'nowrap',
});
const tdStyle = (i, n, { total, inset, extra } = {}) => {
  const v = total ? 6 : 5;
  const edge = total || inset ? 6 : 0;
  return {
    padding: i === 0 ? `${v}px 4px ${v}px ${edge}px` : i === n - 1 ? `${v}px ${edge}px ${v}px 4px` : `${v}px 4px`,
    ...(total ? { background: FILL, borderTop: `1px solid ${LINE_STRONG}`, borderBottom: 'none', fontWeight: 600 } : { borderBottom: `1px solid ${LINE}` }),
    color: INK,
    ...extra,
  };
};
const Table = ({ cols, mt = 8, sec, children }) => (
  <table data-sec={sec} data-part={sec ? 'table' : undefined} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginTop: mt, fontSize: 12, lineHeight: '16px' }}>
    <colgroup>{cols.map((c, i) => <col key={i} style={{ width: c.w }} />)}</colgroup>
    <thead><tr>{cols.map((c, i) => <th key={i} style={thStyle(i, cols.length, c)}>{c.label}</th>)}</tr></thead>
    <tbody>{children}</tbody>
  </table>
);

/* ── the tables' columns ── */
const PHASE_COLS = [{ label: 'Phase', w: 156 }, { label: 'Budget', w: 110, align: 'right' }, { label: 'Actual', w: 110, align: 'right' }, { label: 'Committed', w: 96, align: 'right' }, { label: 'Over / under', w: 112, align: 'right' }, { label: 'Spent', w: 112, align: 'right' }];
const DETAIL_COLS = [{ label: 'Code', w: 40 }, { label: 'Name', w: 124 }, { label: 'Budget', w: 112, align: 'right' }, { label: 'Actual', w: 112, align: 'right' }, { label: 'Committed', w: 92, align: 'right' }, { label: 'Over / under', w: 104, align: 'right' }, { label: 'Spent', w: 112, align: 'right' }];
const TRAVEL_COLS = [{ label: 'Code', w: 40 }, { label: 'Name', w: 76 }, { label: 'Budget', w: 84, align: 'right' }, { label: 'Used', w: 132, align: 'right' }];
const HOURS_COLS = [{ label: 'Code', w: 40 }, { label: 'Name', w: 272 }, { label: 'Budget', w: 100, align: 'right' }, { label: 'Used', w: 156, align: 'right' }, { label: 'Remaining', w: 128, align: 'right' }];
const CO_COLS = [{ label: 'CO', w: 40 }, { label: 'Description', w: 360 }, { label: 'Approved by', w: 150 }, { label: 'Contract change', w: 146, align: 'right' }];

/* ── rows (each renders exactly one <tr>; the layout pass measures them in order) ── */
const PhaseRow = ({ p, total, fs }) => {
  const n = 6;
  const pct = p.budget ? p.actual / p.budget : null;
  const over = p.actual - p.budget;
  const color = statusColor(pct);
  return (
    <tr>
      <td style={tdStyle(0, n, { total })}>{total ? 'Total' : `${p.no} ${p.name}`}</td>
      <td style={tdStyle(1, n, { total, extra: numCell(fs) })}>{money(p.budget)}</td>
      <td style={tdStyle(2, n, { total, extra: numCell(fs) })}>{money(p.actual)}</td>
      <td style={tdStyle(3, n, { total, extra: numCell(fs) })}>{money(p.committed)}</td>
      <td style={tdStyle(4, n, { total, extra: { ...numCell(fs), color: overColor(over) } })}>{money(over, { signed: true })}</td>
      <td style={tdStyle(5, n, { total })}><RatioCell text={pctText(pct)} pct={pct} color={color} barW={48} bold={total} fs={fs} /></td>
    </tr>
  );
};
const DetailRow = ({ r, fs }) => {
  const n = 7;
  const sub = r.kind !== 'code';
  const pct = r.budget ? r.actual / r.budget : null;
  const over = r.actual - r.budget;
  const color = r.assumed ? LINE_STRONG : statusColor(pct);
  const o = (extra) => ({ total: sub, inset: !sub, extra });
  return (
    <tr>
      <td style={tdStyle(0, n, o(sub ? null : { fontFamily: MONO, color: GREY }))}>{r.kind === 'total' ? 'Total' : r.code}</td>
      <td style={tdStyle(1, n, o())}>{r.kind === 'total' ? '' : `${r.name}${r.assumed ? ' *' : ''}`}</td>
      <td style={tdStyle(2, n, o(numCell(fs)))}>{money(r.budget)}</td>
      <td style={tdStyle(3, n, o(numCell(fs)))}>{money(r.actual)}</td>
      <td style={tdStyle(4, n, o(numCell(fs)))}>{money(r.committed)}</td>
      <td style={tdStyle(5, n, o({ ...numCell(fs), color: overColor(over) }))}>{money(over, { signed: true })}</td>
      <td style={tdStyle(6, n, o())}><RatioCell text={pctText(pct)} pct={pct} color={color} textColor={r.assumed ? INK : color} barW={48} bold={sub} fs={fs} /></td>
    </tr>
  );
};
const TravelRow = ({ r, total, fs }) => {
  const n = 4;
  const pct = r.budget > 0 ? r.used / r.budget : null;
  return (
    <tr>
      <td style={tdStyle(0, n, { total, extra: total ? null : { fontFamily: MONO, color: GREY } })}>{total ? 'Total' : r.code}</td>
      <td style={tdStyle(1, n, { total })}>{total ? '' : r.name}</td>
      <td style={tdStyle(2, n, { total, extra: numCell(fs) })}>{money(r.budget)}</td>
      <td style={tdStyle(3, n, { total })}><RatioCell text={money(r.used)} pct={pct} color={statusColor(pct)} textColor={INK} barW={40} bold={total} fs={fs} /></td>
    </tr>
  );
};
/* Labor hours by discipline (#62), laid out like the cost detail: a discipline row carrying
   its subtotal (phase number, name), its codes indented beneath, then the total. */
const HoursRow = ({ r, kind = 'code' }) => {
  const n = 5;
  const sub = kind !== 'code';
  // Hours used against no budget are over it: a full red bar.
  const pct = r.budget > 0 ? r.used / r.budget : r.used > 0 ? 2 : null;
  const left = r.budget - r.used;
  const over = r.used > r.budget + 0.005;
  const o = (extra) => ({ total: sub, inset: !sub, extra });
  return (
    <tr>
      <td style={tdStyle(0, n, o(sub ? null : { fontFamily: MONO, color: GREY }))}>{kind === 'total' ? 'Total' : kind === 'group' ? r.no : r.code}</td>
      <td style={tdStyle(1, n, o())}>{kind === 'total' ? '' : r.name}</td>
      <td style={tdStyle(2, n, o(numCell()))}>{hrs(r.budget)}</td>
      <td style={tdStyle(3, n, o())}><RatioCell text={hrs(r.used)} pct={pct} color={statusColor(pct)} textColor={over ? RED : INK} barW={48} bold={sub} /></td>
      <td style={tdStyle(4, n, o({ ...numCell(), color: left < -0.005 ? RED : INK }))}>{hrs(left)}</td>
    </tr>
  );
};
const CoRow = ({ c, total, sum }) => {
  const n = 4;
  return (
    <tr>
      <td style={tdStyle(0, n, { total, extra: total ? null : { fontFamily: MONO, color: GREY } })}>{total ? 'Total' : c.co_number}</td>
      <td style={tdStyle(1, n, { total })}>{total ? '' : (c.description || '—')}</td>
      <td style={tdStyle(2, n, { total })}>{total ? '' : (c.approved_by || '—')}</td>
      <td style={tdStyle(3, n, { total, extra: numCell() })}>{money(total ? sum : Number(c.contract_change) || 0, { signed: true })}</td>
    </tr>
  );
};
/* A note: the cost code (or the phase, for a phase note) and its name, who wrote it and when,
   then the text. The API returns the author as user_name. */
const NoteCard = ({ n, d }) => {
  const phase = String(n.phase || '').trim();
  const code = String(n.category || '').trim();
  const name = code ? (d.codeName[`${phase}|${code}`] || `Code ${code}`) : (d.phaseName[phase] || 'Phase');
  const by = [n.user_name || n.created_by || n.author, fmtDay(whenDay(n.created_at))].filter(Boolean).join(' · ');
  return (
    <div style={{ padding: '10px 12px', background: FILL, borderRadius: 2 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 10px', fontSize: 12, lineHeight: '16px' }}>
        <span style={{ fontWeight: 600, color: INK }}><span style={{ fontFamily: MONO, color: GREY, fontWeight: 400 }}>{code || phase}</span> {name}</span>
        {by && <span style={{ color: GREY }}>{by}</span>}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, lineHeight: '18px', color: INK, whiteSpace: 'pre-wrap' }}>{n.note}</div>
    </div>
  );
};

/* ── page 1 ── */
const Figure = ({ label, value, color, sub, bar }) => (
  <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 12px', background: FILL, borderRadius: 2, minWidth: 0 }}>
    <div style={{ fontSize: 12, lineHeight: '16px', fontWeight: 700, letterSpacing: '.055em', textTransform: 'uppercase', color: GREY }}>{label}</div>
    {/* 24px rather than the design's 1.05 (23.625px): whole-pixel tiles, same look. */}
    <div style={{ marginTop: 6, ...mono, fontSize: 22.5, lineHeight: '24px', fontWeight: 800, letterSpacing: '-.03em', color: color || INK }}>{value}</div>
    <div style={{ marginTop: 8, fontSize: 12, lineHeight: '18px', color: GREY }}>{sub}</div>
    {bar && (
      <div style={{ marginTop: 'auto', paddingTop: 9 }}>
        <div style={{ height: 5, background: LINE }}>
          {bar.pct > 0 && <div style={{ height: 5, width: `${Math.min(100, bar.pct * 100)}%`, minWidth: bar.min || 1, background: bar.color }} />}
        </div>
      </div>
    )}
  </div>
);
const fig = (s) => <span style={mono}>{s}</span>;
const fig2 = (s) => <span style={{ ...mono, color: INK2 }}>{s}</span>;
const Swatch = ({ color, border, w = 10, h = 10 }) => <span style={{ width: w, height: h, boxSizing: 'border-box', background: color, border: border ? `1px solid ${border}` : undefined }} />;

/* Monthly cost trend — the hand-off's chart geometry (696 × 164; plot L40 R44 T8 B28). Billed
   bars wide behind, spent bars slim in front, labor hours as a line on its own scale. Strokes
   skip crispEdges: Chrome's PDF turns crisp strokes into hairlines that all but vanish on paper
   (the 1px lines already sit on the pixel grid). */
const niceCeil = (x) => {
  if (!(x > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(x)));
  const f = x / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
};
const TrendChart = ({ data }) => {
  const W = CONTENT_W, H = 164, L = 40, R = 44, T = 8, B = 28;
  const pw = W - L - R, ph = H - T - B, yb = T + ph, n = data.length;
  const dStep = niceCeil(Math.max(...data.map((d) => Math.max(d.spent, d.billed))) / 3), dMax = dStep * 3;
  const hs = data.filter((d) => d.hours != null).map((d) => d.hours);
  const hStep = niceCeil((hs.length ? Math.max(...hs) : 0) / 3), hMax = hStep * 3;
  // $1.5M / $250k; a small job's steps keep their decimal ($2.5k) or read in dollars.
  const fd = (v) => (v === 0 ? '$0' : dMax >= 1e6 ? `$${(v / 1e6).toFixed(1)}M`
    : dMax >= 3e3 ? `$${v % 1e3 === 0 ? v / 1e3 : (v / 1e3).toFixed(1)}k` : `$${v.toLocaleString('en-US')}`);
  const fh = (v) => `${v.toLocaleString('en-US')} h`;
  const w = pw / n;
  const wideW = Math.max(3, Math.min(28, Math.round(w - Math.max(4, w * 0.35))));
  const slimW = Math.max(2, Math.round(wideW * 0.45));
  const every = n <= 12 ? 1 : (3 * w >= 36 ? 3 : 6);
  const kids = [];
  const pts = [];
  for (let k = 0; k <= 3; k += 1) {
    const y = Math.round(yb - (k * ph) / 3) + 0.5;
    kids.push(<line key={`g${k}`} x1={L} x2={L + pw} y1={y} y2={y} stroke={k === 0 ? LINE_STRONG : LINE} strokeWidth="1" />);
    if (hs.length) kids.push(<text key={`l${k}`} x={L - 6} y={y + 4} textAnchor="end" fill={GREY}>{fh(k * hStep)}</text>);
    kids.push(<text key={`r${k}`} x={L + pw + 6} y={y + 4} textAnchor="start" fill={GREY}>{fd(k * dStep)}</text>);
  }
  data.forEach((d, i) => {
    const cx = L + i * w + w / 2;
    const hS = (d.spent / dMax) * ph;
    const hB = (d.billed / dMax) * ph;
    if (hB > 0) kids.push(<rect key={`b${i}`} x={Math.round(cx - wideW / 2) + 0.5} y={yb - hB + 0.5} width={wideW - 1} height={Math.max(0.5, hB - 1)} fill={BILLED} stroke={LINE_STRONG} strokeWidth="1" />);
    if (hS > 0) kids.push(<rect key={`s${i}`} x={Math.round(cx - slimW / 2)} y={yb - hS} width={slimW} height={hS} fill={GREEN_SPENT} shapeRendering="crispEdges" />);
    if (d.hours != null) pts.push([cx, yb - (d.hours / hMax) * ph]);
    const lab = every === 1 || (every === 3 ? [1, 4, 7, 10].includes(d.m) : [1, 7].includes(d.m));
    if (lab) {
      kids.push(<line key={`t${i}`} x1={Math.round(cx) + 0.5} x2={Math.round(cx) + 0.5} y1={yb + 1} y2={yb + (d.m === 1 ? 8 : 4)} stroke={LINE_STRONG} strokeWidth="1" />);
      kids.push(<text key={`m${i}`} x={cx} y={yb + 20} textAnchor="middle" fill={GREY}>{d.m === 1 || (every === 1 && i === 0) ? `${MON[d.m - 1]} ’${String(d.y).slice(2)}` : MON[d.m - 1]}</text>);
    }
  });
  if (pts.length > 1) kids.push(<polyline key="hl" points={pts.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')} fill="none" stroke={ORANGE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly cost trend" style={{ display: 'block', fontFamily: MONO, fontSize: 12, fontVariantNumeric: 'tabular-nums', overflow: 'visible' }}>
      {kids}
    </svg>
  );
};

/* Page 1: header, status, cost against budget, eight figures, the trend, and the first
   `phaseCount` phase rows with the total. */
const Page1Body = ({ d, phaseCount }) => {
  const { project, fin } = d;
  const [statusWord, statusCol] = HEALTH[healthKey(fin.pctSpent)];
  const many = (s) => String(s).includes(',');
  const meta = [
    d.job && `#${d.job}`,
    project.client_name,
    project.project_manager && `${many(project.project_manager) ? 'PMs' : 'PM'} ${project.project_manager}`,
    project.construction_manager && `${many(project.construction_manager) ? 'CMs' : 'CM'} ${project.construction_manager}`,
  ].filter(Boolean).join(' · ');
  // Cost against budget: the track is the budget; an overrun and the open POs run past it.
  // The scale leaves 10% headroom past the budget so its mark always sits inside the box.
  const { budget, actual_cost: actual, committed } = fin;
  const scale = Math.max(budget * 1.1, actual + committed);
  const at = (v) => `${(v / scale) * 100}%`;
  const overrun = Math.max(0, actual - budget);
  // Far over budget, the budget mark sits so far left its label would hit the title: it
  // then reads from the mark rightwards instead.
  const markX = scale > 0 ? (budget / scale) * CONTENT_W : 0;
  const budgetLabel = markX >= 330 ? { right: `${(1 - budget / scale) * 100}%`, textAlign: 'right' } : { left: markX + 8 };
  const due = daysUntil(d.endDate, nowOn(d.testDate)); // a test job counts from its test date
  const pct = fin.pctSpent;
  const target = fin.gmPct;
  const hasContract = fin.contract_value > 0;
  const gmBar = !hasContract ? null : fin.actual_gm < 0 ? { pct: 1, color: RED } : { pct: target > 0 ? fin.actual_gm_pct / target : 1, color: GREEN_GOOD };
  const inv = d.collections ? Number(d.collections.invoiced) || 0 : null;
  const col = d.collections ? Number(d.collections.collected) || 0 : null;
  const billedPct = inv != null && hasContract ? inv / fin.contract_value : null;
  // Collected is CE's ratio (collected ÷ invoiced), rounded DOWN to 0.1% so 100.0% only ever
  // means fully collected — the same as the Job Costs tile.
  const colRatio = inv > 0 ? col / inv : null;
  const colDone = colRatio != null && col >= inv - 0.005;
  const colPct = colRatio == null ? null : colDone ? Math.max(1, Math.floor(colRatio * 1000) / 1000) : Math.floor(colRatio * 1000) / 1000;
  const hp = d.hoursTotal.budget > 0 ? d.hoursTotal.used / d.hoursTotal.budget : null;
  const tp = d.travelTotal.budget > 0 ? d.travelTotal.used / d.travelTotal.budget : null;
  const plural = (k) => `${k} day${k === 1 ? '' : 's'}`;
  // The chart's legend names only the series this job has, as the Job Costs tab does.
  const anyBilled = d.chart.some((x) => x.billed > 0);
  const anyHours = d.chart.some((x) => x.hours != null);

  return (
    <>
      <div style={{ paddingBottom: 14, borderBottom: `2px solid ${GREEN_HEAD}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div style={sectionTitle}>E3 Entegral Solutions · Project status report</div>
          <Tag />
        </div>
        <h1 style={{ margin: '8px 0 0', fontFamily: SANS, fontSize: 28, lineHeight: '32px', fontWeight: 600, letterSpacing: '-.02em', color: INK }}>{project.project_name}</h1>
        {/* minHeight: the 12px date's baseline sits half a pixel lower; a whole-pixel row keeps
            everything below on the pixel grid, so the chart's 1px lines print crisp. */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16, marginTop: 6, minHeight: 19, fontSize: 13, lineHeight: '18px', color: INK2 }}>
          <div>{meta}</div>
          {d.asOf && <div style={{ fontSize: 12, color: GREY, whiteSpace: 'nowrap' }}>Cost data as of {fmtDay(d.asOf)}</div>}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '8px 0', borderBottom: `1px solid ${LINE}` }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, lineHeight: '20px', fontWeight: 600, color: statusCol }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusCol }} />{statusWord}
        </div>
        {d.started && <div style={{ fontSize: 12, lineHeight: '16px', color: INK2 }}>Started {fmtDay(d.started)}</div>}
      </div>

      <div style={{ marginTop: 16 }}>
        {budget > 0 ? (
          <>
            <div style={{ position: 'relative', height: 60 }}>
              <div style={{ ...sectionTitle, position: 'absolute', left: 0, top: 0 }}>Cost against budget</div>
              <div style={{ position: 'absolute', top: 0, ...budgetLabel, fontSize: 12, lineHeight: '16px', color: INK, whiteSpace: 'nowrap' }}>Budget <span style={{ fontFamily: MONO, fontWeight: 600 }}>{whole(budget)}</span></div>
              <div style={{ position: 'absolute', left: 0, top: 26, width: at(budget), height: 20, boxSizing: 'border-box', background: FILL, border: `1px solid ${LINE}` }} />
              {actual > 0 && <div style={{ position: 'absolute', left: 0, top: 26, width: at(Math.min(actual, budget)), height: 20, background: GREEN_SPENT }} />}
              {overrun > 0 && <div style={{ position: 'absolute', left: at(budget), top: 26, width: at(overrun), height: 20, background: RED }} />}
              {committed > 0 && <div style={{ position: 'absolute', left: at(Math.max(actual, 0)), top: 26, width: at(committed), height: 20, background: AMBER }} />}
              <div style={{ position: 'absolute', left: `calc(${at(budget)} - 1px)`, top: 18, width: 2, height: 36, background: INK }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', marginTop: 4, fontSize: 12, lineHeight: '16px', color: INK2 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={GREEN_SPENT} />Actual cost <span style={{ fontFamily: MONO, color: INK }}>{whole(actual)}</span></span>
              {overrun > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={RED} />Over budget <span style={{ fontFamily: MONO, color: RED }}>{whole(overrun, { signed: true })}</span></span>}
              {committed > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={AMBER} />Committed POs <span style={{ fontFamily: MONO, color: AMBER }}>{whole(committed)}</span></span>}
            </div>
          </>
        ) : (
          <>
            <div style={sectionTitle}>Cost against budget</div>
            <div style={{ marginTop: 6, fontSize: 13, lineHeight: '18px', color: GREY }}>No budget in CE for this job yet · actual cost {whole(actual)}</div>
          </>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6, marginTop: 12 }}>
        <Figure label="Contract value" value={whole(fin.contract_value)} sub={(
          <div style={mono}>
            {due == null
              ? <div style={{ color: GREY_PH }}>Due date:<br />no schedule yet</div>
              : (
                <>
                  <div style={{ whiteSpace: 'nowrap', color: INK2 }}>Due {fmtDay(localDay(d.endDate))}</div>
                  <div style={{ whiteSpace: 'nowrap', color: due < 0 ? RED : GREY }}>{due > 0 ? `${plural(due)} left` : due === 0 ? 'today' : `${plural(-due)} over`}</div>
                </>
              )}
          </div>
        )} />
        <Figure label="Actual cost" value={pctText(pct)} color={pct > 1 ? RED : INK}
          sub={<>{fig2(whole(actual))}<br />{budget > 0 ? <>of {fig(whole(budget))} budget</> : 'no budget in CE'}</>}
          bar={{ pct, color: statusColor(pct) }} />
        <Figure label="Gross margin" value={hasContract ? pctText(fin.actual_gm_pct) : '—'} color={!hasContract ? INK : fin.actual_gm < 0 ? RED : GREEN_GOOD}
          sub={hasContract ? <>{fig2(whole(fin.actual_gm, { signed: true }))}<br />target {fig(pctText(target))}</> : <>no contract value<br />in Summit</>}
          bar={gmBar} />
        <Figure label="Committed" value={whole(committed)} color={committed > 0 ? AMBER : INK}
          sub="open purchase orders" bar={{ pct: budget > 0 ? committed / budget : 0, color: AMBER, min: 3 }} />
        <Figure label="Billed (CE)" value={pctText(billedPct)}
          sub={inv == null ? <>CE invoices<br />not available</> : <>{fig2(whole(inv))}<br />invoiced</>}
          bar={{ pct: billedPct, color: GREEN_SPENT }} />
        <Figure label="Collected" value={pctText(colPct)}
          sub={inv == null ? <>CE payments<br />not available</> : <>{fig2(whole(col))}<br />received</>}
          bar={{ pct: colPct, color: GREEN_SPENT }} />
        <Figure label="Labor hours" value={pctText(hp)} color={hp > 1 ? RED : INK}
          sub={d.hoursTotal.budget || d.hoursTotal.used
            ? <>{fig2(`${Math.round(d.hoursTotal.used).toLocaleString('en-US')} hrs`)}<br />of {fig(Math.round(d.hoursTotal.budget).toLocaleString('en-US'))} budgeted</>
            : <>no hours pulled<br />from CE yet</>}
          bar={{ pct: hp, color: statusColor(hp) }} />
        <Figure label="Travel" value={pctText(tp)} color={tp > 1 ? RED : INK}
          sub={<>{fig2(whole(d.travelTotal.used))}<br />{d.travelTotal.budget > 0 ? <>of {fig(whole(d.travelTotal.budget))} budget</> : 'no travel budget'}</>}
          bar={{ pct: tp, color: statusColor(tp) }} />
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
          <div style={sectionTitle}>Monthly cost trend</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, lineHeight: '16px', color: INK2 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={GREEN_SPENT} />Spent</span>
            {anyBilled && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={BILLED} border={LINE_STRONG} />Billed (CE)</span>}
            {anyHours && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Swatch color={ORANGE} w={18} h={2} />Labor hours</span>}
          </div>
        </div>
        {d.chart.length ? (
          <>
            <div style={{ marginTop: 8, height: 164 }}><TrendChart data={d.chart} /></div>
            <div style={{ marginTop: 4, fontSize: 12, lineHeight: '16px', color: GREY }}>
              {anyBilled && 'Billed = CE invoices by invoice date, not the month the work was done. '}The first bar includes all cost before {MON[d.chart[0].m - 1]} {d.chart[0].y}.
            </div>
          </>
        ) : <EmptyLine>No monthly cost history pulled from CE yet</EmptyLine>}
      </div>

      <div style={{ marginTop: 12 }}>
        <SectionHead title="Phase summary" note="Spent bar: 100% = phase budget" />
        <Table cols={PHASE_COLS} mt={6} sec="p1-phases">
          {d.phases.slice(0, phaseCount).map((p) => <PhaseRow key={p.no} p={p} fs={d.fs.phase} />)}
          <PhaseRow p={d.phaseTotal} total fs={d.fs.phase} />
        </Table>
      </div>
    </>
  );
};

/* ── pages 2+ ── */
const RunningHead = ({ d }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, paddingBottom: 10, borderBottom: `2px solid ${GREEN_HEAD}`, fontSize: 12, lineHeight: '16px', color: GREY }}>
    <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <span style={{ fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN_HEAD }}>Project status report</span>
      {' · '}<span style={{ color: INK }}>{d.project.project_name}</span>{d.job ? ` · #${d.job}` : ''}
    </div>
    <Tag />
  </div>
);
const Sheet = ({ d, page, pages, children }) => (
  <div className="psr-sheet" style={{ width: PAGE_W, height: PAGE_H, boxSizing: 'border-box', background: '#fff', color: INK, fontFamily: SANS, fontSize: 13, lineHeight: 1.4, position: 'relative', padding: `${PAD_TOP}px ${PAD_X}px 0`, overflow: 'hidden', textWrap: 'pretty' }}>
    {children}
    <div style={{ position: 'absolute', left: PAD_X, right: PAD_X, bottom: 40, display: 'flex', justifyContent: 'space-between', gap: 16, paddingTop: 8, borderTop: `1px solid ${LINE}`, fontSize: 12, lineHeight: '16px', color: GREY }}>
      <div><span style={{ color: RED, fontWeight: 600, letterSpacing: '.08em' }}>CONFIDENTIAL</span> · E3 Entegral Solutions · Project status report{d.job ? ` · #${d.job}` : ''} · Generated {d.generated}</div>
      <div style={{ whiteSpace: 'nowrap' }}>Page {page} of {pages}</div>
    </div>
  </div>
);

/* The flowing sections. kind 'table' (rows break across pages; the head repeats), 'cards'
   (note cards that break between cards), 'block' (never splits). `gap` is the space above a
   section; one that starts a page sits 20px under the running header instead. */
const travelBlock = (d, withNotes) => {
  const travel = (
    <div>
      <div style={sectionTitle}>Travel cost summary</div>
      <Table cols={TRAVEL_COLS}>
        {d.travel.map((r) => <TravelRow key={r.code} r={r} fs={d.fs.travel} />)}
        <TravelRow r={d.travelTotal} total fs={d.fs.travel} />
      </Table>
      <div style={{ marginTop: 8, fontSize: 12, lineHeight: '16px', color: GREY }}>
        Remaining: {money(d.travelTotal.budget - d.travelTotal.used)} of {money(d.travelTotal.budget)}. Bar: used as a share of the line budget.
      </div>
    </div>
  );
  if (!withNotes) return { id: 'travel', kind: 'block', newPage: true, gap: 20, el: <div style={{ width: 332 }}>{travel}</div> };
  return {
    id: 'travel', kind: 'block', newPage: true, gap: 20,
    el: (
      <div style={{ display: 'grid', gridTemplateColumns: '332px 340px', columnGap: 24, alignItems: 'start' }}>
        {travel}
        <div>
          <div style={sectionTitle}>Notes</div>
          {d.notes.length
            ? <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>{d.notes.map((n, i) => <NoteCard key={i} n={n} d={d} />)}</div>
            : <EmptyLine>No notes on this job</EmptyLine>}
        </div>
      </div>
    ),
  };
};
const notesCards = (d) => ({ id: 'notes', kind: 'cards', title: 'Notes', gap: 22, rows: d.notes.map((n, i) => ({ el: <NoteCard key={i} n={n} d={d} /> })) });

const buildFlow = (d, keep, notesInline) => {
  const out = [];
  const rest = d.phases.slice(keep);
  if (rest.length) {
    out.push({ id: 'phases2', kind: 'table', title: 'Phase summary', note: 'Spent bar: 100% = phase budget', cols: PHASE_COLS, mt: 6, gap: 20, continued: true,
      rows: rest.map((p) => ({ el: <PhaseRow key={p.no} p={p} fs={d.fs.phase} /> })) });
  }
  out.push({ id: 'detail', kind: 'table', title: 'Cost detail by phase and cost code', note: 'Spent bar: 100% = line budget', cols: DETAIL_COLS, mt: 8, gap: 20,
    rows: d.detailRows.map((r, i) => ({ el: <DetailRow key={i} r={r} fs={d.fs.detail} />, keepWithNext: r.kind === 'phase', isTotal: r.kind === 'total' })),
    tail: <div style={{ marginTop: 10, fontSize: 12, lineHeight: '16px', color: GREY }}>* Counted at budget until the job closes out, the same as Summit&apos;s Job Costs tab. Spent bar colour: red over 100%, amber 85–100%, green below.</div> });
  // Page 3 opens with travel beside the notes. Notes too long for that follow on their own,
  // full width, and break between cards.
  out.push(travelBlock(d, notesInline));
  if (!notesInline) out.push(notesCards(d));
  const hasHours = d.hours.some((r) => r.budget || r.used);
  out.push(hasHours
    ? { id: 'hours', kind: 'table', title: 'Labor hours summary', note: 'Used bar: 100% = line budget', cols: HOURS_COLS, mt: 8, gap: 22,
      rows: [
        ...d.hoursGroups.flatMap((g) => [
          { el: <HoursRow key={`g${g.no}`} r={g} kind="group" />, keepWithNext: true },
          ...g.lines.map((r) => ({ el: <HoursRow key={`${g.no}.${r.code}`} r={r} /> })),
        ]),
        { el: <HoursRow key="total" r={d.hoursTotal} kind="total" />, isTotal: true },
      ] }
    : { id: 'hours', kind: 'block', gap: 22, el: <div><SectionHead title="Labor hours summary" /><EmptyLine>No labor hours pulled from CE yet</EmptyLine></div> });
  out.push(d.cos.length
    ? { id: 'cos', kind: 'table', title: 'Change orders in CE', cols: CO_COLS, mt: 8, gap: 22,
      rows: [...d.cos.map((c, i) => ({ el: <CoRow key={i} c={c} /> })), { el: <CoRow key="total" total sum={d.coTotal} />, isTotal: true }] }
    : { id: 'cos', kind: 'block', gap: 22, el: <div><SectionHead title="Change orders in CE" /><EmptyLine>No change orders entered in CE for this job</EmptyLine></div> });
  out.push({ id: 'sources', kind: 'block', gap: 20,
    el: (
      <div style={{ fontSize: 12, lineHeight: '16px', color: GREY, maxWidth: 640 }}>
        Costs, budgets, committed POs, labor hours, change orders and invoices come from ComputerEase{d.asOf ? ` as of ${fmtDay(d.asOf)}` : ''}. Gross margin is contract value less actual cost and open POs. Billed and collected are CE invoices and payments. Generated by E3 Summit on {d.generated}.
      </div>
    ) });
  return out;
};

/* One page's share of a section: its head ("(continued)" after a break), the rows, and the
   tail once the section ends. */
const CARDS_STYLE = { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 };
const SectionSlice = ({ s, from, to, continued, withTail, first }) => {
  const style = { marginTop: first ? 20 : s.gap };
  if (s.kind === 'block') return <div style={style}>{s.el}</div>;
  return (
    <div style={style}>
      <SectionHead title={s.title} note={s.note} continued={continued} />
      {s.kind === 'cards'
        ? <div style={CARDS_STYLE}>{s.rows.slice(from, to).map((r) => r.el)}</div>
        : <Table cols={s.cols} mt={s.mt}>{s.rows.slice(from, to).map((r) => r.el)}</Table>}
      {withTail && s.tail}
    </div>
  );
};

/* Every section in full, tagged for the layout pass. */
const FlowMeasure = ({ sections }) => sections.map((s) => {
  if (s.kind === 'block') return <div key={s.id} data-sec={s.id} data-part="block">{s.el}</div>;
  return (
    <div key={s.id}>
      <div data-sec={s.id} data-part="head"><SectionHead title={s.title} note={s.note} continued /></div>
      {s.kind === 'cards'
        ? <div style={CARDS_STYLE}>{s.rows.map((r, i) => <div key={i} data-sec={s.id} data-part="card">{r.el}</div>)}</div>
        : <Table cols={s.cols} mt={s.mt} sec={s.id}>{s.rows.map((r) => r.el)}</Table>}
      {s.tail && <div data-sec={s.id} data-part="tail">{s.tail}</div>}
    </div>
  );
});

/* Pack the sections onto pages from their measured heights (H[id]: block, or head + thead +
   rows[] + tail). Returns one list of slices per page. A row that ends its section brings the
   tail along; a phase heading never ends a page; a total row never starts one alone. */
const paginate = (sections, H, top) => {
  const pages = [];
  let page = [];
  let y = top;
  const flush = () => { if (page.length) pages.push(page); page = []; y = top; };
  sections.forEach((s) => {
    const h = H[s.id];
    if (s.newPage) flush();
    if (s.kind === 'block') {
      if (page.length && y + s.gap + h.block > CONTENT_BOTTOM) flush();
      const first = !page.length;
      page.push({ s, first });
      y += (first ? 20 : s.gap) + h.block;
      return;
    }
    const n = s.rows.length;
    const rh = h.rows;
    const tail = h.tail || 0;
    const need = (j) => rh[j] + (j === n - 1 ? tail : 0);
    let i = 0;
    let continued = !!s.continued;
    while (i < n) {
      if (page.length && y + s.gap + h.head + h.thead + need(i) > CONTENT_BOTTOM) flush();
      const first = !page.length;
      let end = y + (first ? 20 : s.gap) + h.head + h.thead;
      let j = i;
      while (j < n && end + need(j) <= CONTENT_BOTTOM) { end += rh[j]; j += 1; }
      if (j === i) { end += rh[j]; j += 1; } // a row taller than a page still has to go somewhere
      while (j < n && j - i > 1 && (s.rows[j - 1].keepWithNext || (j === n - 1 && s.rows[j].isTotal))) { j -= 1; end -= rh[j]; }
      const done = j >= n;
      page.push({ s, from: i, to: j, continued, withTail: done, first });
      y = end + (done ? tail : 0);
      i = j;
      continued = true;
      if (!done) flush();
    }
  });
  flush();
  return pages;
};

/* Fonts: loadPsrFonts (lib/psrData) — measuring in a fallback face would break the pages in
   the wrong places. */

/* ── Email report: E3 people only. The PDF is built here in the browser from the sheets on
   screen (lib/psrPdf, loaded on demand); the server checks every address is @e3es.com,
   then sends it from noreply@e3es.com with replies to the sender. ── */
const E3_ADDRESS = /^[^\s@]+@e3es\.com$/i;
const MAX_TO = 20;
const MailIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m4 7 8 6 8-6" />
  </svg>
);

const EmailDialog = ({ d, pages, sheetsRef, onClose, onSent }) => {
  const [people, setPeople] = useState([]);
  const [to, setTo] = useState([]); // [{ email, name }]
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [active, setActive] = useState(0);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null); // null | 'pdf' | 'send'
  const inputRef = useRef(null);
  const busy = !!stage;
  const filename = `PSR ${d.job || 'report'} ${d.today || ymd(new Date())}.pdf`;

  useEffect(() => {
    let alive = true;
    getPMList().then((rows) => { if (alive) setPeople((rows || []).filter((p) => E3_ADDRESS.test(p.email || ''))); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const q = text.trim().toLowerCase();
  const taken = (email) => to.some((t) => t.email.toLowerCase() === email.toLowerCase());
  const suggestions = q
    ? people.filter((p) => !taken(p.email) && ((p.name || '').toLowerCase().includes(q) || p.email.toLowerCase().includes(q))).slice(0, 6)
    : [];
  const add = (p) => {
    if (to.length >= MAX_TO) { setError(`Up to ${MAX_TO} people at a time.`); return; }
    setTo((t) => [...t, { email: p.email.toLowerCase(), name: p.name || '' }]);
    setText(''); setActive(0); setError(null);
  };
  // Enter / Tab / comma: the highlighted suggestion, else the typed address (E3 only).
  const commit = () => {
    const v = text.trim().replace(/[,;]+$/, '');
    if (!v) return false;
    if (suggestions[active]) { add(suggestions[active]); return true; }
    if (!E3_ADDRESS.test(v)) { setError(`${v} can’t receive this. E3 addresses only (@e3es.com).`); return true; }
    if (taken(v)) { setText(''); return true; }
    add(people.find((p) => p.email.toLowerCase() === v.toLowerCase()) || { email: v });
    return true;
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      if (commit()) e.preventDefault();
    } else if (e.key === 'Backspace' && !text && to.length) {
      setTo((t) => t.slice(0, -1));
    } else if (e.key === 'ArrowDown' && suggestions.length) {
      e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp' && suggestions.length) {
      e.preventDefault(); setActive((a) => Math.max(a - 1, 0));
    }
  };

  const send = async () => {
    if (busy) return;
    // Whatever is still typed counts (state updates land after this click, so build the list here).
    let list = to;
    const typed = text.trim().replace(/[,;]+$/, '');
    if (typed) {
      const pick = suggestions[active] || (E3_ADDRESS.test(typed) ? (people.find((p) => p.email.toLowerCase() === typed.toLowerCase()) || { email: typed }) : null);
      if (!pick) { setError(`${typed} can’t receive this. E3 addresses only (@e3es.com).`); return; }
      if (!taken(pick.email)) list = [...to, { email: pick.email.toLowerCase(), name: pick.name || '' }];
      setTo(list); setText('');
    }
    if (!list.length) { setError('Add at least one E3 person.'); return; }
    if (list.length > MAX_TO) { setError(`Up to ${MAX_TO} people at a time.`); return; }
    setError(null);
    try {
      setStage('pdf');
      const { buildPsrPdf, toBase64 } = await import('../lib/psrPdf');
      const { bytes, pages: pdfPages } = await buildPsrPdf(sheetsRef.current, {
        title: `Project status report — ${d.project.project_name}${d.job ? ` (#${d.job})` : ''}`,
        author: 'E3 Entegral Solutions',
      });
      setStage('send');
      // The headline figures as the report shows them, for the email's short list.
      const hasContract = d.fin.contract_value > 0;
      const summary = {
        status: HEALTH[healthKey(d.fin.pctSpent)][0],
        spent: pctText(d.fin.pctSpent),
        margin: hasContract ? pctText(d.fin.actual_gm_pct) : '',
        contract: hasContract ? whole(d.fin.contract_value) : '',
        asOf: d.asOf ? fmtDay(d.asOf) : '',
      };
      const res = await emailPsr(d.project.id, {
        to: list.map((t) => t.email), message: note.trim(), filename, pdf_base64: toBase64(bytes), summary, pages: pdfPages,
      });
      onSent((res && res.recipients) || list.length);
    } catch (e) {
      setError(e.message || 'The report couldn’t be sent. Try again.');
      setStage(null);
    }
  };

  const field = { width: '100%', boxSizing: 'border-box', border: '1px solid #D6D2CA', borderRadius: 8, background: '#fff', font: `400 14px ${SANS}`, color: INK };
  return (
    <div className="psr-dialog" role="dialog" aria-modal="true" aria-label="Email this report"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 5, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(31,29,26,.36)' }}>
      <div style={{ width: 520, maxWidth: '100%', background: '#fff', borderRadius: 14, border: '1px solid #E0DDD8', boxShadow: '0 24px 60px -12px rgba(0,0,0,.28)', padding: '18px 20px 16px', fontFamily: SANS, color: INK }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-.01em' }}>Email this report</div>
            <div style={{ marginTop: 3, fontSize: 13.5, color: GREY }}>{d.project.project_name}{d.job ? ` · #${d.job}` : ''} · E3 people only</div>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" style={{ width: 26, height: 26, border: 'none', background: 'transparent', color: GREY, cursor: busy ? 'default' : 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        <label style={{ display: 'block', marginTop: 14, fontSize: 12.5, fontWeight: 600, color: INK2 }}>To</label>
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) { e.preventDefault(); inputRef.current && inputRef.current.focus(); } }}
          style={{ ...field, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 5, padding: '5px 8px', minHeight: 38, cursor: 'text' }}>
          {to.map((t) => (
            <span key={t.email} title={t.email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 24, padding: '0 4px 0 9px', borderRadius: 999, background: '#EEF2EA', color: '#3F5A36', fontSize: 13, fontWeight: 500 }}>
              {t.name || t.email}
              <button type="button" aria-label={`Remove ${t.name || t.email}`} disabled={busy} onClick={() => setTo((all) => all.filter((x) => x.email !== t.email))}
                style={{ width: 18, height: 18, border: 'none', borderRadius: 999, background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
          <input ref={inputRef} value={text} autoFocus disabled={busy}
            onChange={(e) => { setText(e.target.value); setActive(0); setError(null); }} onKeyDown={onKeyDown}
            placeholder={to.length ? '' : 'Name or name@e3es.com'} aria-label="Add a recipient"
            style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', font: `400 14px ${SANS}`, color: INK, background: 'transparent', padding: '3px 2px' }} />
        </div>
        {suggestions.length > 0 && (
          <div role="listbox" style={{ marginTop: 4, border: '1px solid #E0DDD8', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
            {suggestions.map((p, i) => (
              <div key={p.email} role="option" aria-selected={i === active} onMouseDown={(e) => { e.preventDefault(); add(p); }} onMouseEnter={() => setActive(i)}
                style={{ padding: '7px 10px', fontSize: 13.5, cursor: 'pointer', background: i === active ? '#F5F3EF' : '#fff' }}>
                {p.name} <span style={{ color: GREY }}>· {p.email}</span>
              </div>
            ))}
          </div>
        )}

        <label style={{ display: 'block', marginTop: 12, fontSize: 12.5, fontWeight: 600, color: INK2 }}>Message <span style={{ fontWeight: 400, color: GREY }}>(optional)</span></label>
        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} disabled={busy} rows={3}
          placeholder="What should they look at?" style={{ ...field, marginTop: 5, padding: '8px 10px', resize: 'vertical', lineHeight: 1.45 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', background: '#F5F3EF', borderRadius: 8, fontSize: 13.5 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v5h5" /></svg>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</span>
          <span style={{ color: GREY, whiteSpace: 'nowrap' }}>{pages} page{pages === 1 ? '' : 's'}</span>
        </div>

        {error && <div role="alert" style={{ marginTop: 10, fontSize: 13, color: RED, lineHeight: 1.4 }}>{error}</div>}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: GREY }}>From noreply@e3es.com. Replies come to you.</span>
          <button type="button" className="psr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="psr-btn primary" disabled={busy} onClick={send}>
            <MailIcon />{stage === 'pdf' ? 'Preparing PDF…' : stage === 'send' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PsrReportPage = () => {
  const { idOrSlug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [layout, setLayout] = useState(null);
  const measureRef = useRef(null);
  const pagesRef = useRef(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [sentMsg, setSentMsg] = useState('');
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxErr, setXlsxErr] = useState('');
  // Export → Excel: the same figures as a workbook to work with (lib/psrData → lib/psrXlsx).
  const downloadExcel = async () => {
    if (!data || xlsxBusy) return;
    setXlsxBusy(true); setXlsxErr(''); setSentMsg('');
    try {
      await downloadPsrWorkbook(data);
    } catch (e) {
      setXlsxErr(e.message || 'The workbook couldn’t be built.');
    } finally {
      setXlsxBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const base = await loadPsrData(idOrSlug);
        if (!alive) return;
        // Room for figures per column: its width less the cell padding (and the bar, in Used).
        const moneyRows = (rows, [b, a, c, o]) => rows.flatMap((r) => [
          [money(r.budget), b], [money(r.actual), a], [money(r.committed), c], [money(r.actual - r.budget, { signed: true }), o]]);
        const fs = {
          phase: fitFont(moneyRows([...base.phases, base.phaseTotal], [102, 102, 88, 104])),
          detail: fitFont(moneyRows(base.detailRows, [104, 104, 84, 96])),
          travel: fitFont([...base.travel, base.travelTotal].flatMap((r) => [[money(r.budget), 76], [money(r.used), 74]])),
        };
        setData({ ...base, fs });
      } catch (e) {
        if (alive) setError(e.message || 'Could not load this project.');
      }
    })();
    return () => { alive = false; };
  }, [idOrSlug]);

  useEffect(() => {
    let alive = true;
    loadPsrFonts().then(() => { if (alive) setFontsReady(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    document.body.classList.add('psr-open');
    return () => document.body.classList.remove('psr-open');
  }, []);

  // Everything, measured once: every flowing section in full, plus the variants the layout
  // may switch to (notes on their own; travel without them).
  const measureSecs = useMemo(() => (data
    ? [...buildFlow(data, 0, true), notesCards(data), { ...travelBlock(data, false), id: 'travel-alone' }]
    : null), [data]);

  useLayoutEffect(() => {
    if (!data || !fontsReady || layout || !measureRef.current) return;
    const root = measureRef.current;
    const rect = (el) => el.getBoundingClientRect();
    const q = (id, part) => root.querySelector(`[data-sec="${id}"][data-part="${part}"]`);

    // Page 1: as many phase rows as leave room for the total above the footer.
    const p1Top = rect(root.querySelector('[data-p1]')).top;
    const ptbl = q('p1-phases', 'table');
    const ptrs = [...ptbl.tBodies[0].rows];
    const totalH = rect(ptbl).bottom - rect(ptrs[ptrs.length - 1]).top;
    let keep = 0;
    for (let k = data.phases.length; k >= 0; k -= 1) {
      if (rect(ptrs[k]).top - p1Top + totalH <= CONTENT_BOTTOM) { keep = k; break; }
    }

    // Each flowing piece. Row heights run top to top, so shared borders count once.
    const H = {};
    measureSecs.forEach((s) => {
      if (s.kind === 'block') { H[s.id] = { block: rect(q(s.id, 'block')).height }; return; }
      const head = rect(q(s.id, 'head')).height;
      if (s.kind === 'cards') {
        const cards = [...root.querySelectorAll(`[data-sec="${s.id}"][data-part="card"]`)];
        H[s.id] = { head, thead: CARDS_STYLE.marginTop - CARDS_STYLE.gap, rows: cards.map((c) => rect(c).height + CARDS_STYLE.gap), tail: 0 };
        return;
      }
      const tbl = q(s.id, 'table');
      const tb = rect(tbl);
      const trs = [...tbl.tBodies[0].rows];
      H[s.id] = {
        head,
        thead: (trs.length ? rect(trs[0]).top : tb.bottom) - tb.top + s.mt,
        rows: trs.map((tr, i) => (i + 1 < trs.length ? rect(trs[i + 1]).top : tb.bottom) - rect(tr).top),
        tail: s.tail ? rect(q(s.id, 'tail')).bottom - tb.bottom : 0,
      };
    });
    const top = rect(root.querySelector('[data-rh]')).height;
    if (H.phases2) H.phases2 = { ...H.phases2, rows: H.phases2.rows.slice(keep) };
    const notesInline = top + 20 + H.travel.block <= CONTENT_BOTTOM;
    if (!notesInline) H.travel = H['travel-alone'];
    setLayout({ keep, pages: paginate(buildFlow(data, keep, notesInline), H, top) });
  }, [data, fontsReady, layout, measureSecs]);

  const job = data ? data.job : '';
  const autoPrint = params.get('print') === '1';
  useEffect(() => {
    if (!layout || !autoPrint) return undefined;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [layout, autoPrint]);
  useEffect(() => {
    const prev = document.title;
    if (data) document.title = `PSR ${job} — ${data.project.project_name}`;
    return () => { document.title = prev; };
  }, [data, job]);

  const total = layout ? layout.pages.length + 1 : 0;
  const css = `
.psr-shell{position:fixed;inset:0;z-index:3000;overflow:auto;background:#E6E3DE;font-family:${SANS};color:${INK};-webkit-font-smoothing:antialiased;font-feature-settings:normal;letter-spacing:normal;text-rendering:auto}
.psr-bar{position:sticky;top:0;z-index:2;display:flex;align-items:center;gap:12px;padding:10px 20px;background:rgba(255,255,255,.94);backdrop-filter:blur(8px);border-bottom:1px solid #E0DDD8}
.psr-bar .t{flex:1;min-width:0;font-size:13.5px;color:${GREY};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.psr-btn{display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 14px;border-radius:8px;font:600 13.5px ${SANS};cursor:pointer;border:1px solid #E0DDD8;background:#fff;color:${INK}}
.psr-btn.primary{background:${GREEN_SPENT};border-color:${GREEN_SPENT};color:#fff}
.psr-btn:disabled{opacity:.55;cursor:default}
.psr-pages{display:flex;flex-direction:column;align-items:center;gap:24px;padding:24px 16px 48px}
.psr-pages .psr-sheet{box-shadow:0 1px 2px rgba(0,0,0,.06),0 12px 32px -12px rgba(0,0,0,.18);flex:none}
.psr-shell tbody tr:hover{background:none}
@page{size:letter;margin:0}
@media print{
  body.psr-open #root{display:none!important}
  html,body{background:#fff!important;margin:0!important}
  .psr-shell{position:static;overflow:visible;background:none}
  .psr-bar,.psr-measure,.psr-dialog{display:none!important}
  .psr-pages{display:block;padding:0}
  .psr-pages .psr-sheet{box-shadow:none;break-after:page}
  .psr-pages .psr-sheet:last-child{break-after:auto}
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
}`;

  return createPortal(
    <div className="psr-shell" data-psr-ready={layout ? 'true' : undefined}>
      <style>{css}</style>
      <div className="psr-bar">
        <button type="button" className="psr-btn" onClick={() => navigate(`/projects/${idOrSlug}/job-costs`)}>← Job Costs</button>
        <div className="t">{data ? `Project status report · ${data.project.project_name}` : 'Project status report'}</div>
        {sentMsg && <span role="status" style={{ fontSize: 13.5, fontWeight: 600, color: GREEN_GOOD, whiteSpace: 'nowrap' }}>{sentMsg}</span>}
        {xlsxErr && <span role="alert" style={{ fontSize: 13.5, color: RED, whiteSpace: 'nowrap' }}>{xlsxErr}</span>}
        <button type="button" className="psr-btn" disabled={!layout} onClick={() => window.print()}>Print / Save as PDF</button>
        <button type="button" className="psr-btn" disabled={!data || xlsxBusy} onClick={downloadExcel}>{xlsxBusy ? 'Building…' : 'Download Excel'}</button>
        <button type="button" className="psr-btn primary" disabled={!layout} onClick={() => { setSentMsg(''); setEmailOpen(true); }}><MailIcon />Email report</button>
      </div>
      {error && <div style={{ padding: 40, textAlign: 'center', color: RED }}>{error}</div>}
      {!error && !layout && <div style={{ padding: 60, textAlign: 'center', color: GREY }}>Building the report…</div>}
      {data && fontsReady && !layout && (
        <div className="psr-measure" ref={measureRef} aria-hidden="true" style={{ position: 'absolute', left: -10000, top: 0, width: CONTENT_W, visibility: 'hidden', fontFamily: SANS, fontSize: 13, lineHeight: 1.4, color: INK, textWrap: 'pretty' }}>
          <div data-p1=""><Page1Body d={data} phaseCount={data.phases.length} /></div>
          <div data-rh=""><RunningHead d={data} /></div>
          <FlowMeasure sections={measureSecs} />
        </div>
      )}
      {layout && (
        <div className="psr-pages" ref={pagesRef}>
          <Sheet d={data} page={1} pages={total}><Page1Body d={data} phaseCount={layout.keep} /></Sheet>
          {layout.pages.map((slices, pi) => (
            <Sheet key={pi} d={data} page={pi + 2} pages={total}>
              <RunningHead d={data} />
              {slices.map((sl, si) => <SectionSlice key={`${sl.s.id}-${si}`} {...sl} />)}
            </Sheet>
          ))}
        </div>
      )}
      {emailOpen && layout && (
        <EmailDialog d={data} pages={total} sheetsRef={pagesRef} onClose={() => setEmailOpen(false)}
          onSent={(n) => { setEmailOpen(false); setSentMsg(`Sent to ${n} ${n === 1 ? 'person' : 'people'}`); }} />
      )}
    </div>,
    document.body,
  );
};

export default PsrReportPage;
